import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import useActiveFilterGenerator from "app/pages/sales-intelligence/sub-modules/signals/hooks/useActiveFilterGenerator";
import type { TrafficChangesDTO } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficChangesDTO";
import type TrafficChangesPeriod from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficChangesPeriod";
import type TrafficChangesTrend from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficChangesTrend";
import type { TrafficMetric } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficMetrics";
import type TrafficSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficSignalFilters";
import type { ICountryObject } from "app/services/CountryService";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { StyledDropdownLabel } from "../../../../components/shared/SignalsDropdownLabel/styles";
import TrafficSignalTemplate from "../../../../models/traffic/TrafficSignalTemplate";
import type TrafficSignalFiltersViewProps from "../../../../models/view/traffic/TrafficSignalFiltersViewProps";
import SignalTrafficChangesValueFilter from "../../../filters/SignalTrafficChangesValueFilter/SignalTrafficChangesValueFilter";
import SignalTrafficChannelsFilter from "../../../filters/SignalTrafficChannelsFilter/SignalTrafficChannelsFilter";
import SignalTrafficCountryFilter from "../../../filters/SIgnalTrafficCountryFilter/SIgnalTrafficCountryFilter";
import SignalTrafficDirectionsFilter from "../../../filters/SignalTrafficDirectionsFilter/SignalTrafficDirectionsFilter";
import SignalPeriodsFilter from "app/pages/sales-intelligence/sub-modules/signals/components/filters/SignalPeriodsFilter/SignalPeriodsFilter";
import SignalFiltersWrapper from "../../../shared/SignalFiltersWrapper/SignalFiltersWrapper";
import trackingService from "app/pages/sales-intelligence/sub-modules/signals/services/tracking/trackingService";

type AccountTractionChangeFiltersViewProps = TrafficSignalFiltersViewProps & {
    countries: readonly ICountryObject[];
    trafficChanges: Readonly<Partial<TrafficChangesDTO>>;
};

const AccountTractionChangeFiltersView = (props: AccountTractionChangeFiltersViewProps) => {
    const { filters, onFiltersChange } = props;

    const { currentActiveKey, setNextActiveKey } = useActiveFilterGenerator(filters.trafficChanges);

    const handleFilterChange = (updatedFilters: TrafficSignalFilters["trafficChanges"]) => {
        onFiltersChange({
            trafficChanges: {
                ...filters.trafficChanges,
                ...updatedFilters,
            },
        });

        const shouldSetNextActiveKey =
            currentActiveKey && typeof filters.trafficChanges?.[currentActiveKey] === "undefined";
        if (shouldSetNextActiveKey) {
            setNextActiveKey();
        }
    };

    function handlePeriodChange(period: TrafficChangesPeriod): void {
        trackingService.filters.trackTractionPeriodChange(period, props.signalAlias);

        handleFilterChange({
            period,
        });
    }

    function handleDirectionChange(direction: TrafficChangesTrend): void {
        handleFilterChange({
            direction,
        });
    }

    function handleChangeValue(value: number): void {
        handleFilterChange({
            value,
        });
    }

    function handleChannelChange(metric: TrafficMetric): void {
        handleFilterChange({
            metric,
        });
    }

    return (
        <SignalFiltersWrapper>
            <FlexRow
                alignItems="center"
                gap="8px"
            >
                <I18n
                    data-automation="traffic-visits-prefix-text"
                    element={StyledDropdownLabel}
                    translatable={{ key: `si.signals.management.traffic.prefix.website` }}
                />
                <SignalTrafficChannelsFilter
                    selectedValue={filters.trafficChanges?.metric}
                    options={props.trafficChanges.metrics ?? []}
                    autoOpen={currentActiveKey === "metric"}
                    onValueChange={handleChannelChange}
                />

                <I18n
                    data-automation="traffic-signal-country-text"
                    element={StyledDropdownLabel}
                    translatable={{ key: `si.signals.traffic.countryFrom.prefix` }}
                />
                <SignalTrafficCountryFilter
                    filters={filters}
                    allowedCountries={props.countries}
                    onFiltersChange={onFiltersChange}
                />
            </FlexRow>

            <FlexRow
                alignItems="center"
                gap="8px"
            >
                <SignalTrafficDirectionsFilter
                    selectedValue={filters.trafficChanges?.direction}
                    options={props.trafficChanges.directions ?? []}
                    onValueChange={handleDirectionChange}
                />

                <I18n
                    data-automation="changing-traction-prefix-text"
                    element={StyledDropdownLabel}
                    translatable={{ key: `si.signals.traffic.changing-transaction.prefix` }}
                />
                <SignalPeriodsFilter<TrafficChangesPeriod>
                    selectedValue={filters.trafficChanges?.period}
                    options={props.trafficChanges.periods ?? []}
                    onValueChange={handlePeriodChange}
                />

                <I18n
                    data-automation="changing-traction-at-least-prefix-text"
                    element={StyledDropdownLabel}
                    translatable={{
                        key: `si.signals.traffic.changing-transaction-at-least.prefix`,
                    }}
                />

                <SignalTrafficChangesValueFilter
                    selectedValue={filters.trafficChanges?.value}
                    options={TrafficSignalTemplate.TRAFFIC_CHANGE_VALUES}
                    autoOpen={currentActiveKey === "value"}
                    onValueChange={handleChangeValue}
                />
            </FlexRow>
        </SignalFiltersWrapper>
    );
}

export default AccountTractionChangeFiltersView;
